import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { estimateService } from '../../services/estimateService';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

const EstimatesPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, estimate: null });
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    const fetchEstimates = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await estimateService.getAllEstimates();
        
        if (response && response.status === 'success' && response.data && Array.isArray(response.data.estimates)) {
          setEstimates(response.data.estimates);
        } else if (Array.isArray(response)) {
          // Handle direct array response
          setEstimates(response);
        } else {
          console.error('Unexpected data structure from estimates API:', response);
          throw new Error('Failed to parse estimates data - unexpected structure');
        }

      } catch (err) {
        console.error("Error fetching estimates:", err);
        setError(err.message || 'Could not load estimates.');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
        fetchEstimates();
    }
  }, [token]);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleDateString();
    } catch (e) {
        return dateString;
    }
  };

  const handleDeleteClick = (estimate) => {
    setDeleteModal({ isOpen: true, estimate });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.estimate) return;
    
    setIsDeleting(true);
    try {
      await estimateService.deleteEstimate(deleteModal.estimate.id);
      setEstimates(prev => prev.filter(est => est.id !== deleteModal.estimate.id));
      setDeleteModal({ isOpen: false, estimate: null });
    } catch (err) {
      console.error('Error deleting estimate:', err);
      setError(err.message || 'Failed to delete estimate');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, estimate: null });
  };

  // Check if user has permission to delete
  const canDelete = user && ['admin', 'manager'].includes(user.role);

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-brand-text-dark">Estimates Management</h1>
          <p className="text-lg text-gray-600 mt-2">
            View, create, and manage customer estimates.
          </p>
        </div>
        <Link 
          to="/admin/estimates/new" 
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-150 ease-in-out"
        >
          Create New Estimate
        </Link>
      </header>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading estimates...</p>
        ) : estimates.length === 0 && !error ? (
          <p className="p-6 text-gray-500">No estimates found. Get started by creating one!</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estimate ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {estimates.map((estimate) => (
                <tr key={estimate.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <Link to={`/admin/estimates/view/${estimate.id}`} className="text-blue-600 hover:text-blue-800">
                      {estimate.estimateNumber || estimate.id}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{estimate.client?.username || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(estimate.estimateDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(estimate.expiryDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">${parseFloat(estimate.totalAmount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ 
                      estimate.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      estimate.status === 'sent_to_client' ? 'bg-blue-100 text-blue-800' :
                      estimate.status === 'accepted_by_client' ? 'bg-green-100 text-green-800' :
                      estimate.status === 'rejected_by_client' ? 'bg-red-100 text-red-800' :
                      estimate.status === 'converted_to_invoice' ? 'bg-purple-100 text-purple-800' :
                      estimate.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {estimate.status ? estimate.status.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN'}
                    </span>
                    {estimate.status === 'converted_to_invoice' && estimate.convertedInvoiceId && (
                      <Link to={`/admin/invoices/view/${estimate.convertedInvoiceId}`} className="ml-2 text-xs text-purple-600 hover:text-purple-800">
                        (View Invoice)
                      </Link>
                    )}
                  </td>                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link to={`/admin/estimates/view/${estimate.id}`} className="text-green-600 hover:text-green-900 mr-3">View</Link>
                    <Link to={`/admin/estimates/edit/${estimate.id}`} className="text-indigo-600 hover:text-indigo-900 mr-3">Edit</Link>
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteClick(estimate)}
                        className="text-red-600 hover:text-red-900"
                        disabled={isDeleting}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Estimate"
        message={`Are you sure you want to delete estimate ${deleteModal.estimate?.estimateNumber || deleteModal.estimate?.id}? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default EstimatesPage; 